import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import { UseMutationOptions, useQueryClient } from 'react-query';
import { productAttributesEndpoint } from './use-get-attribute-dictionary-api';

interface Params {
  typeId: number;
  catalogCategoryIds: number[];
}

type TData = number[];
type TError = AxiosError;
type TVariables = Params;

type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

// PUT api/v1/cms/master-product-attributes/types/:typeId/categories
export function useReplaceAttributeTypeCategoriesApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ typeId, catalogCategoryIds }: TVariables): Promise<TData> => {
    try {
      const response = await http.put(`${productAttributesEndpoint}/types/${typeId}/categories`, {
        catalogCategoryIds,
      });
      return response.data.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${productAttributesEndpoint}/types/categories`],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([`${productAttributesEndpoint}/types`]);
      queryClient.invalidateQueries([`${productAttributesEndpoint}/dictionary`]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
